// src/pages/public/StylistDirectoryPage.tsx
import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import PublicSeoPage from './PublicSeoPage';
import { useSeoInventory, usePublicDirectory } from './usePublicSeoData';
import { statesWithInventory } from './publicPageHelpers';
import { getSeoContext } from '../../seo/seoContext';
import { buildStylistDirectoryModel } from '../../seo/core/pageModels';
import { analyzeFacetedUrl, applyFacetsToMetadata } from '../../seo/core/facets';
import { ROUTES } from '../../seo/core/urls';
import type { PublicPageModel } from '../../seo/types';

/**
 * `/stylists` — the national public directory.
 *
 * This is the page the authenticated `/client/browse` experience was never able
 * to be: reachable without a session, with its filters expressed in the URL so
 * a filtered view is shareable and bookmarkable.
 *
 * Because filters are now in the URL, faceted-search protection applies: any
 * parameterised variant is noindex and canonicalises back to a clean page
 * (promoted to the matching location/service landing page where one exists).
 */
function StylistDirectoryPage() {
  const ctx = getSeoContext();
  const [searchParams] = useSearchParams();
  const { inventory, loading: inventoryLoading } = useSeoInventory();

  const filters = useMemo(
    () => ({
      state: searchParams.get('state') || undefined,
      city: searchParams.get('city') || undefined,
      serviceCategory: searchParams.get('service') || undefined,
      limit: 24,
    }),
    [searchParams]
  );

  const { directory, loading: directoryLoading } = usePublicDirectory(filters);

  const model = useMemo<PublicPageModel | null>(() => {
    const states = statesWithInventory(inventory);
    const built = buildStylistDirectoryModel(
      {
        stylists: directory.stylists,
        totals: inventory?.totals || {},
        topStates: states.slice(0, 12),
        topServices: (inventory?.services || []).slice(0, 13),
      },
      ctx
    ) as PublicPageModel;

    const analysis = analyzeFacetedUrl(ROUTES.stylistDirectory(), searchParams, {
      baseIndexable: built.metadata.indexable,
    });

    return { ...built, metadata: applyFacetsToMetadata(built.metadata, analysis, ctx) };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inventory, directory, searchParams.toString(), ctx.environment, ctx.siteUrl]);

  return <PublicSeoPage model={model} loading={inventoryLoading || directoryLoading} />;
}

export default StylistDirectoryPage;
